import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiUser, FiMail, FiLock, FiEye, FiEyeOff,
  FiShoppingBag, FiShield, FiCheckCircle, FiHeadphones,
} from 'react-icons/fi';
import { useGoogleLogin } from '@react-oauth/google';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { googleAuth } from '../services/api';
import './RegisterPage.css';

const ROLE_REDIRECT = { buyer: '/', vendor: '/vendor/dashboard' };

const BUYER_BENEFITS = [
  'Browse 50,000+ products from verified vendors',
  'Secure checkout with multiple payment options',
  'Easy returns & 30-day refund guarantee',
  'Exclusive member deals and coupon codes',
];

const SELLER_BENEFITS = [
  'Set up your store in minutes — completely free',
  'Reach millions of buyers across Pakistan',
  'Powerful dashboard with real-time analytics',
  'Dedicated seller support & dispute resolution',
];

const TRUST_BADGES = [
  { icon: <FiShield size={18} />,      label: 'Secure' },
  { icon: <FiCheckCircle size={18} />, label: 'Verified' },
  { icon: <FiHeadphones size={18} />,  label: 'Support' },
];

export default function RegisterPage() {
  const { registerUser } = useAuth();
  const navigate = useNavigate();

  const [role, setRole] = useState('buyer');
  const [form, setForm] = useState({
    name: '', email: '', password: '', confirm: '',
    storeName: '', terms: false,
  });
  const [showPass, setShowPass]       = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading]         = useState(false);
  const [socialLoading, setSocialLoading] = useState('');
  const [errors, setErrors]           = useState({});

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (res) => {
      setSocialLoading('google');
      try {
        const { data } = await googleAuth({ accessToken: res.access_token });
        if (data.token) {
          localStorage.setItem('token', data.token);
          window.location.href = ROLE_REDIRECT[data.user.role] || '/';
        }
      } catch {
        toast.error('Google sign-up failed. Please try again.');
      } finally {
        setSocialLoading('');
      }
    },
    onError: () => toast.error('Google sign-up failed.'),
  });

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: val }));
    if (errors[field]) setErrors((er) => ({ ...er, [field]: '' }));
  };

  const validate = () => {
    const errs = {};

    if (!form.name.trim())
      errs.name = 'Full name is required';
    else if (form.name.trim().length < 2)
      errs.name = 'Name must be at least 2 characters';

    if (!form.email.trim())
      errs.email = 'Email address is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs.email = 'Enter a valid email address';

    if (!form.password)
      errs.password = 'Password is required';
    else if (form.password.length < 6)
      errs.password = 'Password must be at least 6 characters';

    if (!form.confirm)
      errs.confirm = 'Please confirm your password';
    else if (form.password !== form.confirm)
      errs.confirm = 'Passwords do not match';

    if (role === 'vendor' && !form.storeName.trim())
      errs.storeName = 'Store name is required';

    if (!form.terms)
      errs.terms = 'You must agree to the Terms of Service';

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        role,
      };
      if (role === 'vendor') payload.storeName = form.storeName.trim();

      const data = await registerUser(payload);
      toast.success('Welcome to Fivora!');
      navigate(ROLE_REDIRECT[data.user.role] || '/', { replace: true });
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Registration failed. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const benefits = role === 'vendor' ? SELLER_BENEFITS : BUYER_BENEFITS;

  return (
    <div className="rp-page">

      {/* ── Left Panel ──────────────────────────────────────────────────────── */}
      <div className="rp-left">
        <div className="rp-left-inner">

          {/* Logo */}
          <div className="rp-logo">
            <img src="/fivora-logo.png" alt="Fivora" className="rp-logo-img" />
            <div className="rp-logo-text">Fivora</div>
          </div>

          {/* Heading */}
          <div className="rp-tagline-block">
            <h2 className="rp-tagline">
              {role === 'vendor' ? 'Start Selling Today' : 'Join Fivora'}
            </h2>
            <p className="rp-tagline-sub">
              {role === 'vendor'
                ? "Launch your store on Pakistan's fastest-growing marketplace and reach thousands of buyers."
                : 'Create a free account and unlock the best deals from hundreds of verified vendors.'}
            </p>
          </div>

          {/* Benefits */}
          <ul className="rp-benefits">
            {benefits.map((b) => (
              <li key={b} className="rp-benefit-item">
                <span className="rp-benefit-dot">✓</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>

          {/* Trust badges */}
          <div className="rp-trust">
            {TRUST_BADGES.map(({ icon, label }) => (
              <div key={label} className="rp-trust-badge">
                <span className="rp-trust-icon">{icon}</span>
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Blobs */}
        <div className="rp-blob rp-blob-1" aria-hidden="true" />
        <div className="rp-blob rp-blob-2" aria-hidden="true" />
      </div>

      {/* ── Right Panel ─────────────────────────────────────────────────────── */}
      <div className="rp-right">
        <motion.div
          className="rp-form-wrap"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
        >
          <div className="rp-form-header">
            <h1 className="rp-form-title">Create Account</h1>
            <p className="rp-form-sub">Join Fivora — it's free and takes less than a minute</p>
          </div>

          <form className="rp-form" onSubmit={handleSubmit} noValidate>

            {/* ── Role Selector ────────────────────────────────────────────── */}
            <div className="rp-role-group">
              <label className="form-label">I want to…</label>
              <div className="rp-role-cards">
                {[
                  { value: 'buyer',  emoji: '🛍️', title: 'I want to Shop',  sub: 'Browse & buy products' },
                  { value: 'vendor', emoji: '🏪', title: 'I want to Sell',   sub: 'Open my own store' },
                ].map(({ value, emoji, title, sub }) => (
                  <button
                    key={value}
                    type="button"
                    className={`rp-role-card${role === value ? ' active' : ''}`}
                    onClick={() => {
                      setRole(value);
                      setErrors((er) => ({ ...er, storeName: '' }));
                    }}
                  >
                    <span className="rp-role-emoji">{emoji}</span>
                    <span className="rp-role-title">{title}</span>
                    <span className="rp-role-sub">{sub}</span>
                    {role === value && <span className="rp-role-check">✓</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Full Name ─────────────────────────────────────────────────── */}
            <div className="form-group">
              <label className="form-label" htmlFor="rp-name">Full Name</label>
              <div className="rp-input-wrap">
                <FiUser className="rp-input-icon" aria-hidden="true" />
                <input
                  id="rp-name"
                  type="text"
                  className={`form-input rp-input${errors.name ? ' error' : ''}`}
                  placeholder="e.g. Ahmed Khan"
                  value={form.name}
                  onChange={set('name')}
                  autoComplete="name"
                  autoFocus
                />
              </div>
              {errors.name && <span className="form-error">{errors.name}</span>}
            </div>

            {/* ── Email ─────────────────────────────────────────────────────── */}
            <div className="form-group">
              <label className="form-label" htmlFor="rp-email">Email Address</label>
              <div className="rp-input-wrap">
                <FiMail className="rp-input-icon" aria-hidden="true" />
                <input
                  id="rp-email"
                  type="email"
                  className={`form-input rp-input${errors.email ? ' error' : ''}`}
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={set('email')}
                  autoComplete="email"
                />
              </div>
              {errors.email && <span className="form-error">{errors.email}</span>}
            </div>

            {/* ── Password row ─────────────────────────────────────────────── */}
            <div className="rp-two-col">
              {/* Password */}
              <div className="form-group">
                <label className="form-label" htmlFor="rp-password">Password</label>
                <div className="rp-input-wrap">
                  <FiLock className="rp-input-icon" aria-hidden="true" />
                  <input
                    id="rp-password"
                    type={showPass ? 'text' : 'password'}
                    className={`form-input rp-input rp-input-pr${errors.password ? ' error' : ''}`}
                    placeholder="Min 6 characters"
                    value={form.password}
                    onChange={set('password')}
                    autoComplete="new-password"
                  />
                  <button type="button" className="rp-toggle-pass" onClick={() => setShowPass(v => !v)} tabIndex={-1}
                    aria-label={showPass ? 'Hide password' : 'Show password'}>
                    {showPass ? <FiEyeOff size={15} /> : <FiEye size={15} />}
                  </button>
                </div>
                {errors.password && <span className="form-error">{errors.password}</span>}
              </div>

              {/* Confirm Password */}
              <div className="form-group">
                <label className="form-label" htmlFor="rp-confirm">Confirm Password</label>
                <div className="rp-input-wrap">
                  <FiLock className="rp-input-icon" aria-hidden="true" />
                  <input
                    id="rp-confirm"
                    type={showConfirm ? 'text' : 'password'}
                    className={`form-input rp-input rp-input-pr${errors.confirm ? ' error' : ''}`}
                    placeholder="Repeat password"
                    value={form.confirm}
                    onChange={set('confirm')}
                    autoComplete="new-password"
                  />
                  <button type="button" className="rp-toggle-pass" onClick={() => setShowConfirm(v => !v)} tabIndex={-1}
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}>
                    {showConfirm ? <FiEyeOff size={15} /> : <FiEye size={15} />}
                  </button>
                </div>
                {errors.confirm && <span className="form-error">{errors.confirm}</span>}
              </div>
            </div>

            {/* ── Store Name (vendor only) ──────────────────────────────────── */}
            <AnimatePresence>
              {role === 'vendor' && (
                <motion.div
                  className="form-group"
                  key="storeName"
                  initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                  animate={{ opacity: 1, height: 'auto', marginBottom: 0 }}
                  exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                  transition={{ duration: 0.22 }}
                  style={{ overflow: 'hidden' }}
                >
                  <label className="form-label" htmlFor="rp-store">
                    <FiShoppingBag size={13} style={{ marginRight: 5 }} />
                    Store Name
                  </label>
                  <input
                    id="rp-store"
                    type="text"
                    className={`form-input${errors.storeName ? ' error' : ''}`}
                    placeholder="e.g. Ahmed's Electronics"
                    value={form.storeName}
                    onChange={set('storeName')}
                    autoComplete="organization"
                  />
                  {errors.storeName && <span className="form-error">{errors.storeName}</span>}
                  <span className="form-hint">This will be your public store name visible to buyers</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── Terms checkbox ───────────────────────────────────────────── */}
            <div className="rp-terms-group">
              <label className={`rp-terms${errors.terms ? ' rp-terms-error' : ''}`}>
                <input
                  type="checkbox"
                  className="rp-checkbox"
                  checked={form.terms}
                  onChange={set('terms')}
                />
                <span className="rp-checkbox-box" />
                <span className="rp-terms-text">
                  I agree to the{' '}
                  <button type="button" className="rp-policy-link">Terms of Service</button>
                  {' '}and{' '}
                  <button type="button" className="rp-policy-link">Privacy Policy</button>
                </span>
              </label>
              {errors.terms && <span className="form-error rp-terms-msg">{errors.terms}</span>}
            </div>

            {/* ── Submit ───────────────────────────────────────────────────── */}
            <button
              type="submit"
              className="btn btn-primary btn-block rp-submit-btn"
              disabled={loading}
            >
              {loading
                ? <><span className="spinner spinner-sm" /> Creating account…</>
                : `Create ${role === 'vendor' ? 'Seller' : ''} Account`}
            </button>
          </form>

          <div className="social-divider"><span>or sign up with</span></div>
          <div className="social-buttons">
            <button
              type="button"
              className="social-btn social-btn-google"
              onClick={() => handleGoogleLogin()}
              disabled={!!socialLoading}
            >
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              {socialLoading === 'google' ? 'Connecting…' : 'Sign up with Google'}
            </button>
          </div>

          <p className="rp-signin-link">
            Already have an account?{' '}
            <Link to="/login">Sign In</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
